import { useCallback } from "react";
import { ChangedCells } from "@components/spreadsheet/SpreadsheetGrid";
import { ColumnDef, SpreadsheetData } from "@components/spreadsheet/types";
import { AccrualImporterRow } from "./types";
import { propagateSharedField } from "./useAccrualImporterState";
import {
    withDerivedAccrualLineAmount,
    isAmountDrivingAccrualField,
} from "./accrualImporterLineAmount.helpers";

export function useAccrualImporterChangeHandler({
    setRows,
    columns,
    createRow,
}: {
    setRows: React.Dispatch<React.SetStateAction<AccrualImporterRow[]>>;
    columns: ColumnDef[];
    createRow: () => AccrualImporterRow;
}) {
    return useCallback(
        (newData: SpreadsheetData, changedCells: ChangedCells) => {
            setRows((prevRows) => {
                const updated: AccrualImporterRow[] = newData.map((row, idx) =>
                    idx < prevRows.length
                        ? (row as AccrualImporterRow)
                        : { ...createRow(), ...row },
                );

                for (const [rowIdx, colIdx] of changedCells) {
                    const col = columns[colIdx];
                    if (!col || rowIdx >= updated.length) {
                        continue;
                    }

                    if (isAmountDrivingAccrualField(col.field)) {
                        updated[rowIdx] = withDerivedAccrualLineAmount(
                            updated[rowIdx],
                        );
                    }

                    propagateSharedField({
                        rows: updated,
                        prevRows,
                        rowIndex: rowIdx,
                        field: col.field as keyof AccrualImporterRow,
                    });
                }

                return updated.map((row) => withDerivedAccrualLineAmount(row));
            });
        },
        [setRows, columns, createRow],
    );
}
